/**
 * <s-stream> wraps an uploaded video and plays it as an adaptive stream (HLS, data-hls): it starts at a quality the
 * connection can handle, steps up to sharp quality as soon as it can, and briefly steps down instead of freezing.
 * iPhone/iPad/Mac Safari stream natively; other browsers use the small hls.js library (loaded once, only when needed).
 * The <video>'s own MP4 <source> stays as the fallback if streaming is unavailable.
 * API: start() begins streaming (safe to call often); useFile(url) locks to one MP4 (Quality menu); useAuto() returns to streaming.
 * data-autostart: start right away (first episode / first feed slide).
 */
const HLS_LIBRARY = 'https://cdn.jsdelivr.net/npm/hls.js@1/dist/hls.min.js';
let libraryPromise = null;

function loadLibrary() {
  if (window.Hls) return Promise.resolve();
  if (!libraryPromise) {
    libraryPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = HLS_LIBRARY;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.append(script);
    });
  }
  return libraryPromise;
}

function nativeStreaming(video) {
  // Apple devices stream natively and efficiently. Android browsers that claim native support are often slow, so they use hls.js.
  return Boolean(video.canPlayType('application/vnd.apple.mpegurl')) && !/Android/i.test(navigator.userAgent);
}

class SStream extends HTMLElement {
  #hls = null;
  #mode = 'idle';
  #unlisten = null;

  get video() {
    return this.querySelector('video');
  }

  connectedCallback() {
    const video = this.video;
    if (!video || !this.dataset.hls) return;
    // Get the library ready early so switching episodes doesn't wait for it.
    if (!nativeStreaming(video)) loadLibrary().catch(() => {});
    if (this.hasAttribute('data-autostart')) this.start();
  }

  disconnectedCallback() {
    this.#destroy();
  }

  start() {
    const video = this.video;
    const url = this.dataset.hls;
    if (!video || !url || this.#mode !== 'idle') return;
    this.#mode = 'starting';
    if (nativeStreaming(video)) {
      this.#swap(video, () => { video.src = url; });
      this.#mode = 'native';
      return;
    }
    loadLibrary().then(() => {
      const Hls = window.Hls;
      if (this.#mode !== 'starting' || !Hls?.isSupported()) {
        if (this.#mode === 'starting') this.#mode = 'file';
        return;
      }
      // Only the video being watched downloads: a video that isn't playing gets just its first few seconds ready
      // (so the swipe starts instantly), then waits. Paused videos stop downloading so they don't slow the one on screen.
      // Careful quality choices: start on the lightest version, step up only when the connection clearly has room,
      // never above 720p (Shopify's 1080p peaks near 15 Mbps), and keep a bigger reserve loaded ahead.
      const hls = new Hls({
        capLevelToPlayerSize: true,
        startLevel: 0,
        autoStartLoad: false,
        abrBandWidthFactor: 0.8,
        abrBandWidthUpFactor: 0.5,
        maxBufferLength: 45,
        maxMaxBufferLength: 90,
        backBufferLength: 30,
      });
      this.#hls = hls;
      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        const levels = data.levels || [];
        // Shopify makes 1080p, 720p and 480p: when there are three or more, skip the heaviest (1080p) in Auto.
        if (levels.length >= 3) {
          const byBitrate = levels.map((level, index) => ({ index, bitrate: level.bitrate || 0 })).sort((a, b) => b.bitrate - a.bitrate);
          const secondHeaviest = byBitrate[1];
          hls.autoLevelCapping = levels.reduce((cap, level, index) => ((level.bitrate || 0) <= secondHeaviest.bitrate ? Math.max(cap, index) : cap), -1);
        }
        // The lightest version first, so the first picture appears fast.
        let lightest = 0;
        levels.forEach((level, index) => {
          if ((level.bitrate || 0) < (levels[lightest].bitrate || 0)) lightest = index;
        });
        hls.startLevel = lightest;
      });
      // Start/stop downloading only when the state really changes; restarting an active download would cause stalls.
      let loading = false;
      const resume = () => {
        if (loading || this.#hls !== hls) return;
        loading = true;
        hls.startLoad(-1);
      };
      const rest = () => {
        if (!loading || this.#hls !== hls) return;
        loading = false;
        hls.stopLoad();
      };
      hls.on(Hls.Events.MANIFEST_PARSED, resume);
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        if (video.paused && !video.seeking) rest();
      });
      video.addEventListener('play', resume);
      video.addEventListener('playing', resume);
      video.addEventListener('seeking', resume);
      video.addEventListener('pause', rest);
      this.#unlisten = () => {
        video.removeEventListener('play', resume);
        video.removeEventListener('playing', resume);
        video.removeEventListener('seeking', resume);
        video.removeEventListener('pause', rest);
      };
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          loading = false;
          resume();
          return;
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
          return;
        }
        // Streaming can't continue: fall back to the MP4 file.
        this.#destroy();
        this.#mode = 'file';
        video.removeAttribute('src');
        video.load();
      });
      this.#swap(video, () => {
        hls.loadSource(url);
        hls.attachMedia(video);
      });
      this.#mode = 'hls';
    }).catch(() => {
      if (this.#mode === 'starting') this.#mode = 'file';
    });
  }

  // Quality menu picked a fixed size.
  useFile(url) {
    const video = this.video;
    if (!video) return;
    this.#swap(video, () => {
      this.#destroy();
      video.src = url;
    });
    this.#mode = 'file-locked';
  }

  // Quality menu picked Auto.
  useAuto() {
    const video = this.video;
    if (!video) return;
    if (this.#mode === 'hls' || this.#mode === 'native') return;
    this.#destroy();
    this.#mode = 'idle';
    this.start();
  }

  // Changes the video's source but keeps the viewer's place and play/pause state.
  #swap(video, change) {
    const time = video.currentTime || 0;
    const wasPlaying = !video.paused && !video.ended;
    change();
    if (!time && !wasPlaying) return;
    video.addEventListener('loadedmetadata', () => {
      if (time) video.currentTime = time;
      if (wasPlaying) video.play().catch(() => {});
    }, { once: true });
    if (wasPlaying) video.play().catch(() => {});
  }

  #destroy() {
    this.#unlisten?.();
    this.#unlisten = null;
    this.#hls?.destroy();
    this.#hls = null;
  }
}

if (!customElements.get('s-stream')) customElements.define('s-stream', SStream);
